import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User as SupabaseUser, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Define local User type since DB types may not be synced yet
interface User {
  id: string;
  email: string;
  name: string;
  stage_name?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  role: 'ARTISTA' | 'AUTOR' | 'EDITORA' | 'ADMIN';
  verification_status: 'PENDENTE' | 'APROVADO' | 'REPROVADO';
  avatar_url?: string | null;
  banned_at?: string | null;
  banned_until?: string | null;
  ban_reason?: string | null;
  created_at: string;
  updated_at: string;
}

type UserRole = 'ARTISTA' | 'AUTOR' | 'EDITORA' | 'ADMIN';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  needsProfileCompletion: boolean;
  signUp: (email: string, password: string, name: string, role: UserRole, whatsapp?: string, stageName?: string, document?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);

  const fetchUserProfile = async (userId: string): Promise<User | null> => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching profile:", error);
        return null;
      }

      // Check if user is banned
      if (data?.banned_at) {
        const isBanned = !data.banned_until || new Date(data.banned_until) > new Date();
        if (isBanned) {
          // Sign out the user immediately
          await supabase.auth.signOut();
          const banMessage = data.banned_until 
            ? `Sua conta está suspensa até ${new Date(data.banned_until).toLocaleDateString("pt-BR")}. Motivo: ${data.ban_reason || "Não informado"}`
            : `Sua conta está suspensa permanentemente. Motivo: ${data.ban_reason || "Não informado"}`;
          toast.error(banMessage);
          return null;
        }
      }

      // Check if profile needs completion (Google OAuth users without whatsapp)
      if (data && !data.whatsapp) {
        setNeedsProfileCompletion(true);
      } else {
        setNeedsProfileCompletion(false);
      }

      return data as User | null;
    } catch (error) {
      console.error("Error in fetchUserProfile:", error);
      return null;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        
        if (session?.user) {
          // Defer profile fetch to avoid deadlock
          setTimeout(() => {
            fetchUserProfile(session.user.id).then(profile => {
              setUser(profile);
              setIsLoading(false);
            });
          }, 0);
        } else {
          setUser(null);
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        fetchUserProfile(session.user.id).then(profile => {
          setUser(profile);
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, name: string, role: UserRole, whatsapp?: string, stageName?: string, document?: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            name,
            role,
            whatsapp: whatsapp || null,
            stage_name: stageName || null,
            document: document || null,
          },
        },
      });

      if (error) throw error;

      // Update whatsapp, stage_name, and document in profile if provided
      if (data.user) {
        const updateData: { whatsapp?: string; stage_name?: string; document?: string } = {};
        if (whatsapp) updateData.whatsapp = whatsapp;
        if (stageName) updateData.stage_name = stageName;
        if (document) updateData.document = document;
        
        if (Object.keys(updateData).length > 0) {
          await supabase
            .from("profiles")
            .update(updateData)
            .eq("id", data.user.id);
        }
      }

      return { error: null };
    } catch (error) {
      console.error("SignUp error:", error);
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Check if user is banned
      if (data.user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("banned_at, banned_until, ban_reason")
          .eq("id", data.user.id)
          .single();

        if (profile?.banned_at) {
          const isBanned = !profile.banned_until || new Date(profile.banned_until) > new Date();
          if (isBanned) {
            // Sign out the user immediately
            await supabase.auth.signOut();
            const banMessage = profile.banned_until 
              ? `Sua conta está suspensa até ${new Date(profile.banned_until).toLocaleDateString("pt-BR")}. Motivo: ${profile.ban_reason || "Não informado"}`
              : `Sua conta está suspensa permanentemente. Motivo: ${profile.ban_reason || "Não informado"}`;
            throw new Error(banMessage);
          }
        }
      }

      return { error: null };
    } catch (error) {
      console.error("SignIn error:", error);
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
    } catch (error) {
      console.error("SignOut error:", error);
    }
  };

  const refreshUser = async () => {
    if (session?.user) {
      const profile = await fetchUserProfile(session.user.id);
      setUser(profile);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        needsProfileCompletion,
        signUp,
        signIn,
        signOut,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
